import React from 'react';
import { connect } from 'react-redux';
import { Link } from 'react-router-dom';
import classNames from 'classnames';
import {
  Alert,
  Badge,
  Button,
  Card,
  CardBody,
  CardText,
  Col,
  Collapse,
  Form,
  FormGroup,
  Input,
  InputGroup,
  InputGroupAddon,
  Label,
  Row
} from 'reactstrap';
import privateView from '../../components/hocs/privateView';
import Analytics from '../../services/Analytics';
import { CardHeaderWithToolbars, Title, Toolbars as ToolbarsContainer } from '../../components/Card/CardHeaderWithToolbars';
import { FontAwesomeIcon } from '../../components/Icons';
import FilterContainer from '../../components/Container/FilterContainer/FilterContainer';
import PaginationSummaries from '../../components/Paginations/PaginationSummaries';
import Paginations from '../../components/Paginations';
import { Tbl, TblHead, TblBody } from '../../components/Tbl';
import { perPageOptions } from '../../constants/kickavenue';
import { generateNumber } from '../../utils';
import { apiClient } from '../../utils/Api';
import DateToHuman from '../../components/DateToHuman';

const dataHeader = [
  'No.',
  'Name',
  'Active',
  'Updated',
  'Action'
];

const defaultParams = {
  keyword: '',
  active: '',
  sort: 'updated_at_desc',
  per_page: 50
};

const defaultPagination = {
  from: 0,
  to: 0,
  total: 0,
  currentPage: 1,
  lastPage: 1,
  perPage: defaultParams.per_page
};

class VariantGroupIndex extends React.Component {
  constructor(props){
    super(props);
    this._isMountedFlag = false;
    this.state = {
      _params: {
        ...defaultParams
      },
      _filterOpen: false,
      _isFetchList: false,
      _list: [],
      _pagination: {
        ...defaultPagination
      },
      _alertErrorOpen: false,
      _errorMessage: ''
    };
  }

  componentDidMount(){
    this._isMountedFlag = true;
    const { email } = this.props.injectedProps;
    this._getList(this.state._params);
    Analytics.recordPageView('variant_group_list', email, {
      url: window.location.href,
      location: this.props.location,
      match: this.props.match
    });
  }

  componentWillUnmount(){
    this._isMountedFlag = false;
  }

  _safeSetState = payload => {
    if(this._isMountedFlag){
      this.setState(payload);
    }
  }

  _parseSort = (sortValue = defaultParams.sort) => {
    const chunks = String(sortValue).split('_');
    const sortOrder = chunks.pop() || 'desc';
    const sortBy = chunks.join('_') || 'updated_at';
    return {
      sort_by: sortBy,
      sort_order: sortOrder
    };
  }

  _sanitizeParams = rawParams => {
    const { sort, ...restParams } = rawParams;
    const { sort_by, sort_order } = this._parseSort(sort);
    const params = {
      ...restParams,
      sort_by,
      sort_order,
      per_page: Number(restParams.per_page || defaultParams.per_page)
    };
    return {
      params: Object.keys(params)
      .filter(key => params[key] !== '' && params[key] !== null && params[key] !== undefined)
      .reduce((obj, key) => ({...obj, [key]: params[key]}), {})
    };
  }

  _normalizeListResponse = (responseData = {}, rawParams = {}) => {
    const rootData = responseData || {};

    let rows = [];
    let pagination = {
      ...defaultPagination,
      perPage: Number(rawParams.per_page || defaultParams.per_page),
      currentPage: 1
    };

    if(Array.isArray(rootData.data)){
      rows = [...rootData.data];
      const perPage = Number(rootData.per_page || rawParams.per_page || defaultParams.per_page);
      const currentPage = Number(rootData.current_page || 1);
      const from = Number(rootData.from || (rows.length ? ((currentPage - 1) * perPage) + 1 : 0));
      const to = Number(rootData.to || (rows.length ? from + rows.length - 1 : 0));
      const total = Number(rootData.total || rows.length);
      const lastPage = Number(rootData.last_page || (perPage > 0 ? Math.max(1, Math.ceil(total / perPage)) : currentPage));

      pagination = {
        from,
        to,
        total,
        currentPage,
        lastPage,
        perPage
      };
    }
    else if(rootData.data && Array.isArray(rootData.data.data)){
      const nestedData = rootData.data;
      rows = [...nestedData.data];
      const perPage = Number(nestedData.per_page || rawParams.per_page || defaultParams.per_page);
      const currentPage = Number(nestedData.current_page || 1);
      const from = Number(nestedData.from || (rows.length ? ((currentPage - 1) * perPage) + 1 : 0));
      const to = Number(nestedData.to || (rows.length ? from + rows.length - 1 : 0));
      const total = Number(nestedData.total || rootData.total || rows.length);
      const lastPage = Number(nestedData.last_page || (perPage > 0 ? Math.max(1, Math.ceil(total / perPage)) : currentPage));

      pagination = {
        from,
        to,
        total,
        currentPage,
        lastPage,
        perPage
      };
    }

    return {
      rows,
      pagination
    };
  }

  _getList = (rawParams = {}) => {
    this._safeSetState({
      _isFetchList: true,
      _alertErrorOpen: false,
      _errorMessage: ''
    });

    apiClient().get('/admins/variant-groups', this._sanitizeParams(rawParams))
    .then(({ data }) => {
      const normalizedData = this._normalizeListResponse(data, rawParams);
      this._safeSetState({
        _list: normalizedData.rows,
        _pagination: normalizedData.pagination,
        _alertErrorOpen: false,
        _errorMessage: ''
      });
    })
    .catch(err => {
      this._safeSetState({
        _alertErrorOpen: true,
        _errorMessage: err?.response?.data?.message || 'Failed to load variant groups. Please try again.'
      });
    })
    .finally(() => {
      this._safeSetState({ _isFetchList: false });
    });
  }

  _handleToggleFilterPanel = () => {
    this.setState(prevState => ({ _filterOpen: !prevState._filterOpen }));
  }

  _handleKeywordChange = ({ target: { value } }) => {
    this.setState(prevState => ({
      _params: {
        ...prevState._params,
        keyword: value
      }
    }));
  }

  _handleFilterSelectChange = ({ target: { name, value } }) => {
    this.setState(prevState => {
      const nextParams = {
        ...prevState._params,
        [name]: value
      };
      delete nextParams.page;
      return {
        _params: nextParams
      };
    }, () => this._getList(this.state._params));
  }

  _handleActiveFilterChange = ({ target: { value } }) => {
    this.setState(prevState => ({
      _params: {
        ...prevState._params,
        active: value
      }
    }));
  }

  _handleApplyActiveFilter = e => {
    e.preventDefault();
    const nextParams = {
      ...this.state._params
    };
    delete nextParams.page;
    this.setState({
      _params: nextParams
    }, () => this._getList(this.state._params));
  }

  _handleSubmitSearch = e => {
    e.preventDefault();
    const nextParams = {
      ...this.state._params
    };
    delete nextParams.page;
    this.setState({
      _params: nextParams
    }, () => this._getList(this.state._params));
  }

  _handleRefresh = e => {
    e.preventDefault();
    this._getList(this.state._params);
  }

  _handleResetFilter = e => {
    e.preventDefault();
    this.setState({
      _params: {
        ...defaultParams
      },
      _filterOpen: false
    }, () => this._getList(this.state._params));
  }

  _handleGoto = page => {
    const nextParams = {
      ...this.state._params,
      page
    };
    this.setState({
      _params: nextParams
    }, () => this._getList(this.state._params));
  }

  _renderFilterPanel = () => {
    const { _params } = this.state;
    return (
      <FilterContainer>
        <Collapse isOpen={this.state._filterOpen}>
          <CardBody className="filter-container__card-body">
            <Form onSubmit={this._handleSubmitSearch}>
              <FormGroup row>
                <Col xs={12} md={6}>
                  <InputGroup>
                    <Input
                      type="text"
                      name="keyword"
                      value={_params.keyword}
                      onChange={this._handleKeywordChange}
                      placeholder="Search variant group name"
                    />
                    <InputGroupAddon addonType="append">
                      <Button color="primary" disabled={this.state._isFetchList}>Search</Button>
                    </InputGroupAddon>
                  </InputGroup>
                </Col>
              </FormGroup>
            </Form>
            <hr />
            <Form className="form-filter" onSubmit={this._handleApplyActiveFilter}>
              <Row>
                <Col xs={12} md={6}>
                  <p>Filter By :</p>
                  <div className="d-flex" style={{flexDirection: 'column'}}>
                    <div className="d-flex" style={{padding: '0px 1rem'}}>
                      <FormGroup className="d-flex" style={{flexDirection: 'column', alignItems: 'flex-start'}}>
                        <Label className="mb-2" style={{fontWeight: 700}}>
                          Active
                        </Label>
                        <div className="d-flex ml-2 mr-2">
                          <FormGroup check className="mr-2">
                            <Label check>
                              <Input
                                type="radio"
                                name="active"
                                value=""
                                checked={_params.active === ''}
                                onChange={this._handleActiveFilterChange}
                              /> All
                            </Label>
                          </FormGroup>
                          <FormGroup check className="mr-2">
                            <Label check>
                              <Input
                                type="radio"
                                name="active"
                                value="true"
                                checked={_params.active === 'true'}
                                onChange={this._handleActiveFilterChange}
                              /> Active
                            </Label>
                          </FormGroup>
                          <FormGroup check className="mr-2">
                            <Label check>
                              <Input
                                type="radio"
                                name="active"
                                value="false"
                                checked={_params.active === 'false'}
                                onChange={this._handleActiveFilterChange}
                              /> Inactive
                            </Label>
                          </FormGroup>
                        </div>
                      </FormGroup>
                    </div>
                    <FormGroup className="mb-2" style={{alignSelf: 'flex-end'}}>
                      <Button color="primary" disabled={this.state._isFetchList}>Apply Filter</Button>
                    </FormGroup>
                  </div>
                </Col>
              </Row>
            </Form>
            <hr />
            <Form inline className="form-filter">
              <FormGroup className="mb-2 mr-3">
                <Label className="mr-sm-2">Sort By</Label>
                <Input
                  type="select"
                  name="sort"
                  value={_params.sort}
                  onChange={this._handleFilterSelectChange}
                >
                  <option value="updated_at_desc">Last Updated</option>
                  <option value="created_at_desc">Newest</option>
                </Input>
              </FormGroup>
              <FormGroup className="mb-2 mr-3">
                <Label className="mr-sm-2">Show</Label>
                <Input
                  type="select"
                  name="per_page"
                  value={_params.per_page}
                  onChange={this._handleFilterSelectChange}
                >
                  {perPageOptions.map((option) => (
                    <option key={option.value} value={option.value}>{option.label}</option>
                  ))}
                </Input>
              </FormGroup>
            </Form>
          </CardBody>
        </Collapse>
      </FilterContainer>
    );
  }

  render(){
    const { _list, _pagination } = this.state;
    const summaryTotal = _pagination.total;

    return(
      <div className="animated fadeIn">
        <Row>
          <Col xs={12}>
            <Card>
              <CardHeaderWithToolbars>
                <Title>
                  <FontAwesomeIcon iconType="align-justify" /> <span>Variant Group List</span>
                </Title>
                <ToolbarsContainer>
                  <Link
                    to="/variant_groups/create"
                    className="btn btn-sm btn-primary with-toolbars__toolbars__btn"
                    title="Create variant group"
                  >
                    <FontAwesomeIcon iconType="plus" />
                  </Link>
                  <Button
                    title="Refresh"
                    className="with-toolbars__toolbars__btn"
                    size="sm"
                    color="info"
                    onClick={this._handleRefresh}
                    disabled={this.state._isFetchList}
                  >
                    <FontAwesomeIcon iconType="refresh" />
                  </Button>
                  <Button
                    title="Reset filter"
                    className="with-toolbars__toolbars__btn"
                    size="sm"
                    color="warning"
                    onClick={this._handleResetFilter}
                    disabled={this.state._isFetchList}
                  >
                    <FontAwesomeIcon iconType="ban" />
                  </Button>
                  <Button
                    title="Search and Filter Data"
                    size="sm"
                    className={classNames('with-toolbars__toolbars__btn', { 'btn-danger': this.state._filterOpen })}
                    onClick={this._handleToggleFilterPanel}
                  >
                    <i className={classNames('fa', { 'fa-sliders': !this.state._filterOpen, 'fa-minus': this.state._filterOpen })}></i>
                  </Button>
                </ToolbarsContainer>
              </CardHeaderWithToolbars>
              {this._renderFilterPanel()}
              <CardBody>
                <CardText>
                  In this section will display variant groups.
                </CardText>
                <Alert
                  color="danger"
                  isOpen={this.state._alertErrorOpen}
                  toggle={() => this.setState({ _alertErrorOpen: false })}
                >
                  {this.state._errorMessage}
                </Alert>
                <PaginationSummaries
                  from={_pagination.from}
                  to={_pagination.to}
                  total={summaryTotal}
                />
                <div className="table-responsive" ref={tableRef => this.tableRef = tableRef}>
                  <Tbl>
                    <TblHead dataHeader={dataHeader} />
                    <TblBody
                      isLoad={this.state._isFetchList}
                      hasRow={_list.length > 0}
                      columnCount={dataHeader.length}
                      renderChildren={() => _list.map((item, index) => {
                        const rowNumber = generateNumber(_pagination.perPage, _pagination.currentPage, index);
                        return (
                          <tr key={item.id}>
                            <th className="v-center" scope="row">{rowNumber}</th>
                            <td className="v-center">{item.name || '-'}</td>
                            <td className="v-center">
                              <Badge color={item.active ? 'success' : 'secondary'}>
                                {item.active ? 'Active' : 'Inactive'}
                              </Badge>
                            </td>
                            <td className="v-center">
                              {item.updated_at ? (
                                <DateToHuman value={item.updated_at} id={`variant_group_updated_${item.id}`} />
                              ) : '-'}
                            </td>
                            <td className="v-center">
                              <Link to={`/variant_groups/${item.id}`} className="btn btn-link">Details</Link>
                            </td>
                          </tr>
                        );
                      })}
                    />
                  </Tbl>
                </div>
                <Paginations
                  size="sm"
                  lastPage={_pagination.lastPage}
                  perPage={_pagination.perPage}
                  total={summaryTotal}
                  maxPage={8}
                  currentPage={_pagination.currentPage}
                  handleGotoCallback={this._handleGoto}
                  onCurrentPageChanged={() => {
                    if(this.tableRef){
                      window.scrollTo({
                        top: this.tableRef.offsetTop + 25,
                        behavior: 'smooth'
                      });
                    }
                  }}
                />
              </CardBody>
            </Card>
          </Col>
        </Row>
      </div>
    );
  }
}

const mapStateToProps = ({ auth: { isFetch, roles, isLogged, id, email } }) => ({
  guardData: { isFetch, roles, isLogged, id, email }
});

const enhance = connect(mapStateToProps);

export default enhance(privateView(VariantGroupIndex, ['superadministrator', 'administrator', 'inventory_admin']));
